// What Astra did this turn, in words (#3154).
//
// The chat's `activity` envelope names the tools that ran — raw ids like
// `get_profile` and `list_fit_candidates`. Those ids are plumbing: the
// student read them under every answer, and the tutorial QA gate read them
// off the recordings. The app shows words, never its codes (same class as
// #3137). The wire keeps the ids (BUILDER_WRITE_TOOLS and the backend guards
// key on them); only the render goes through here.
//
// One entry per concierge tool. tests/cloud_functions/counselor_agent/
// test_tool_names_never_shown_3154.py pins these keys to concierge_tools.TOOLS in
// both directions, so a new tool cannot ship without its words. An id this
// map does not know still renders as GENERIC_LABEL, never as itself.

export const GENERIC_LABEL = 'Looked into your Stratia data';
export const CONFIRM_LABEL = 'Got a change ready for you to confirm';

export const ACTIVITY_LABELS = Object.freeze({
    // Reads: the student's own record
    get_profile: 'Read your profile',
    get_college_list: 'Read your college list',
    get_fit_analysis: 'Checked your fit',
    get_score_sending: 'Checked whether you send your scores',
    list_fit_candidates: 'Looked for schools that fit you',
    get_college_major_chances: 'Checked your chances by major',
    reported_decision_records: 'Read reported admission results',
    preview_fit: 'Previewed your fit',
    find_similar_schools: 'Looked for similar schools',
    get_major_map: 'Read your major map',
    get_major_strategy: 'Read your major strategy',
    get_university_majors: "Read the school's majors",
    compare_major_across_schools: 'Compared the major across schools',
    get_deadlines: 'Checked your deadlines',
    get_season_moments: 'Checked what this season holds',
    get_aid_steps: 'Checked your aid steps',
    whats_next: 'Worked out what comes next',
    get_university_readiness: 'Checked your readiness',
    get_roadmap_item: 'Read a roadmap item',
    get_timeline: 'Read your timeline',
    get_activity_ideas: 'Read your activity ideas',
    get_roadmap: 'Read your roadmap',
    get_essays: 'Read your essays',
    get_scholarships: 'Checked scholarships',
    get_aid_packages: 'Read your aid packages',
    get_cost_picture: 'Checked the cost picture',
    university_directory: 'Looked through the school directory',
    search_universities: 'Searched for schools',
    get_university: 'Read the school’s profile',
    summer_programs: 'Looked at summer programs',
    news_stories: 'Read Stratia News',
    get_builder_status: 'Checked your projects',
    get_invention: 'Read your invention',
    get_pitch: 'Read your pitch',
    get_study: 'Read your paper',
    // Writes: each one is proposed as a card the student confirms
    add_college: 'Added a school',
    add_colleges: 'Added schools',
    remove_college: 'Removed a school',
    set_intended_majors: 'Updated your intended majors',
    set_major_choice: 'Updated your major choice',
    set_score_sending: 'Updated whether you send your scores',
    add_roadmap_tasks: 'Added roadmap tasks',
    remove_roadmap_task: 'Removed a roadmap task',
    set_item_note: 'Saved a note',
    move_roadmap_item: 'Moved a roadmap item',
    set_roadmap_item_status: 'Updated a roadmap item',
    accept_suggestion: 'Accepted a suggestion',
    save_research: 'Saved research to your notebook',
    generate_major_map: 'Built your major map',
    rank_college_majors: 'Ranked majors at your schools',
    generate_major_strategy: 'Built your major strategy',
    generate_activity_ideas: 'Came up with activity ideas',
    recompute_fit: 'Refreshed your fit',
    // Build and Brainstorm / File a patent
    start_builder_interview: 'Started your project interview',
    builder_answer: 'Saved your answer',
    add_to_builder_answer: 'Added to your answer',
    rewrite_builder_answer: 'Rewrote your answer',
    request_builder_draft: 'Started your draft',
    add_prior_art: 'Added prior art',
    export_invention: 'Exported your invention',
    request_specification: 'Started your specification',
    request_prior_art_search: 'Started a prior-art search',
    record_filing: 'Recorded your filing',
    add_filing_to_application: 'Added your filing to your application',
    review_prior_art_match: 'Reviewed a prior-art match',
    // Write a paper
    start_study: 'Started your paper',
    set_study_venue: 'Set where your paper goes',
    study_answer: 'Saved your answer',
    add_to_study_answer: 'Added to your answer',
    rewrite_study_answer: 'Rewrote your answer',
    pick_study_area: 'Picked your research area',
    request_study_pointers: 'Found reading pointers',
    add_reading_entry: 'Added to your reading list',
    record_study_submission: 'Recorded your submission',
    record_study_publication: 'Recorded your publication',
});

/** A snake_case token anywhere: how a tool id looks. Prose never has one. */
export const SNAKE_ID = /\b[a-z0-9]+(?:_[a-z0-9]+)+\b/i;

const CONFIRM_SUFFIX = /\s*\(needs your confirmation\)\s*$/;

/** One activity entry as words. Entries arrive as a tool id, a tool id with
 *  "(needs your confirmation)", or a finished action already in words
 *  ("✓ Add UC Davis"), which passes through. */
export function activityLabel(entry) {
    const raw = String(entry ?? '').trim();
    if (!raw) return '';
    if (CONFIRM_SUFFIX.test(raw)) return CONFIRM_LABEL;
    if (Object.prototype.hasOwnProperty.call(ACTIVITY_LABELS, raw)) return ACTIVITY_LABELS[raw];
    // Anything carrying an id (a snake_case token) is plumbing we have no
    // words for: say something true and generic rather than show it.
    return SNAKE_ID.test(raw) ? GENERIC_LABEL : raw;
}

/** The status line under a reply: each entry in words, repeats dropped
 *  (two reads of the same kind say the same thing once). */
export function activityLine(activity) {
    const seen = [];
    for (const a of activity || []) {
        const label = activityLabel(a);
        if (label && !seen.includes(label)) seen.push(label);
    }
    return seen.join(' · ');
}
